import { useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, eur } from "@/lib/api";
import { toast } from "sonner";
import { PinConfirmDialog } from "@/components/common/PinPad";
import { Input } from "@/components/ui/input";
import { QrCode, Download, Bell, ReceiptEuro, Printer, LockKeyhole, AlertTriangle } from "lucide-react";

const STATUS = {
  LIBRE: { label: "Libre", dot: "bg-stone-500", card: "bg-[#0D1814] border-[#1B3028]" },
  OCCUPEE: { label: "Occupée", dot: "bg-blue-400", card: "bg-[#111F1A] border-[#1B3028]" },
  EN_PREPARATION: { label: "Commande en préparation", dot: "bg-[#C69C4A]", card: "bg-[#1A1810] border-[#C69C4A]/50" },
  PRETE: { label: "Commande prête", dot: "bg-emerald-400", card: "bg-[#0E1A15] border-emerald-500/40" },
  REGLEE: { label: "Réglée — à clôturer", dot: "bg-emerald-500", card: "bg-[#0E1A15] border-emerald-500/60" },
  DEMANDE_SERVEUR: { label: "Demande serveur", dot: "bg-red-500", card: "bg-[#1C1210] border-red-500/60" },
};

export default function TablesGrid({ tables, slug, onRefresh }) {
  const [qrTable, setQrTable] = useState(null);
  const [coversBusy, setCoversBusy] = useState(null);
  const setCovers = async (num, val) => {
    if (val < 0) return;
    setCoversBusy(num);
    try { await api.setCovers(slug, num, val); onRefresh?.(); } catch (e) { toast.error(e?.response?.data?.detail || "Erreur couverts"); }
    finally { setCoversBusy(null); }
  };
  const [billTable, setBillTable] = useState(null);
  const [bill, setBill] = useState(null);
  const [loadingBill, setLoadingBill] = useState(false);
  const [loadingSheet, setLoadingSheet] = useState(false);
  const [closing, setClosing] = useState(null);
  const [forceTarget, setForceTarget] = useState(null); // { num, remaining }
  const [forceReason, setForceReason] = useState("");
  const [forcePinOpen, setForcePinOpen] = useState(false);
  const canvasWrapRef = useRef(null);

  const closeTable = async (num) => {
    setClosing(num);
    try {
      const res = await api.closeTable(slug, num);
      if (res.status === "CLOSED") toast.success(`Table ${num} clôturée · nouvelle session au prochain client`);
      else toast.info(`Table ${num} déjà libre`);
      onRefresh?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "unpaid") {
        // Unpaid table: force-close requires a reason + Manager/Owner PIN authorization.
        setForceTarget({ num, remaining: detail.remaining });
        setForceReason("");
      } else {
        toast.error("Erreur lors de la clôture");
      }
    }
    finally { setClosing(null); }
  };

  const runForceClose = async (authorizedBy, pin) => {
    setForcePinOpen(false);
    const num = forceTarget?.num;
    try {
      const reason = [forceReason.trim(), authorizedBy ? `Autorisé par ${authorizedBy}` : ""].filter(Boolean).join(" · ");
      await api.closeTable(slug, num, { force: true, reason, pin });
      toast.success(`Table ${num} clôturée avec impayé (motif enregistré)`);
      setForceTarget(null); setForceReason("");
      onRefresh?.();
    } catch (e) { toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Erreur lors de la clôture forcée"); }
  };

  const downloadSheet = async () => {
    setLoadingSheet(true);
    try {
      const blob = await api.qrSheet(slug, window.location.origin);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("Erreur génération PDF"); }
    finally { setLoadingSheet(false); }
  };

  const openBill = async (tbl) => {
    setBillTable(tbl);
    setBill(null);
    setLoadingBill(true);
    try {
      const data = await api.getTableBill(slug, tbl.number);
      setBill(data);
    } catch (e) { toast.error("Erreur addition"); }
    finally { setLoadingBill(false); }
  };

  const qrUrl = qrTable ? `${window.location.origin}/restaurant/${slug}/table/${qrTable.number}` : "";

  const downloadQR = () => {
    const canvas = canvasWrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `qr-table-${qrTable.number}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button data-testid="qr-sheet-btn" onClick={downloadSheet} disabled={loadingSheet} className="rounded-full bg-[#C69C4A] hover:bg-[#B38B3A] text-[#0C1713] font-semibold">
          {loadingSheet ? <span className="w-4 h-4 mr-2 rounded-full border-2 border-[#0C1713] border-t-transparent animate-spin inline-block" /> : <Printer className="w-4 h-4 mr-2" />}
          Imprimer tous les QR (PDF)
        </Button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-3">
        {tables.map((tbl) => {
          const meta = STATUS[tbl.status] || STATUS.LIBRE;
          return (
            <div key={tbl.id} data-testid={`table-card-${tbl.number}`} className={`rounded-2xl border p-4 ${meta.card} flex flex-col`}>
              <div className="flex items-start justify-between">
                <span className="font-serif-display text-2xl text-stone-100">{String(tbl.number).padStart(2, "0")}</span>
                {tbl.status === "DEMANDE_SERVEUR" && <Bell className="w-4 h-4 text-red-500 animate-pulse-ring rounded-full" />}
              </div>
              <div className="flex items-center gap-1.5 mt-2">
                <span className={`w-2 h-2 rounded-full ${meta.dot}`} />
                <span className="text-[11px] text-stone-400 leading-tight">{meta.label}</span>
              </div>
              {tbl.session_open && (
                <div data-testid={`table-total-${tbl.number}`} className="mt-2 text-sm font-bold text-stone-100">
                  {eur(tbl.total || 0)}
                </div>
              )}
              {tbl.session_open && (
                <div data-testid={`table-covers-${tbl.number}`} className="mt-2 flex items-center gap-1.5">
                  <span className="text-[11px] text-stone-400">👥</span>
                  <button data-testid={`covers-dec-${tbl.number}`} onClick={() => setCovers(tbl.number, (tbl.covers || 0) - 1)} disabled={coversBusy === tbl.number || !(tbl.covers > 0)} className="w-6 h-6 rounded-md bg-[#0C1713] border border-[#1B3028] text-stone-300 disabled:opacity-40 leading-none">−</button>
                  <span className="text-sm font-bold text-stone-100 w-6 text-center">{tbl.covers ?? "—"}</span>
                  <button data-testid={`covers-inc-${tbl.number}`} onClick={() => setCovers(tbl.number, (tbl.covers || 0) + 1)} disabled={coversBusy === tbl.number} className="w-6 h-6 rounded-md bg-[#0C1713] border border-[#1B3028] text-stone-300 disabled:opacity-40 leading-none">+</button>
                  <span className="text-[10px] text-stone-500">couv.</span>
                </div>
              )}
              <button
                data-testid={`table-qr-btn-${tbl.number}`}
                onClick={() => setQrTable(tbl)}
                className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#C69C4A] hover:text-[#B38B3A] font-semibold"
              >
                <QrCode className="w-3.5 h-3.5" /> QR code
              </button>
              <button
                data-testid={`table-bill-btn-${tbl.number}`}
                onClick={() => openBill(tbl)}
                className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-stone-400 hover:text-stone-100 font-semibold"
              >
                <ReceiptEuro className="w-3.5 h-3.5" /> Addition
              </button>
              {tbl.session_open ? (
                <button
                  data-testid={`table-close-btn-${tbl.number}`}
                  onClick={() => closeTable(tbl.number)}
                  disabled={closing === tbl.number}
                  className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-red-400 hover:text-red-300 font-semibold disabled:opacity-50"
                >
                  <LockKeyhole className="w-3.5 h-3.5" /> Clôturer la table
                </button>
              ) : (
                <button
                  data-testid={`table-open-btn-${tbl.number}`}
                  onClick={async () => { try { await api.openTable(slug, tbl.number); toast.success(`Table ${tbl.number} ouverte · nouveau service`); onRefresh?.(); } catch { toast.error("Erreur lors de l'ouverture"); } }}
                  className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-emerald-400 hover:text-emerald-300 font-semibold"
                >
                  <LockKeyhole className="w-3.5 h-3.5" /> Ouvrir la table
                </button>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={!!qrTable} onOpenChange={(o) => !o && setQrTable(null)}>
        <DialogContent data-testid="qr-dialog" className="max-w-sm rounded-3xl bg-[#FBF9F5]">
          <DialogTitle className="sr-only">QR Table {qrTable?.number}</DialogTitle>
          <DialogDescription className="sr-only">Code QR d'accès au menu pour la table {qrTable?.number}</DialogDescription>
          <div className="text-center pt-2">
            <h2 className="font-serif-display text-2xl text-[#123026]">Table {qrTable?.number}</h2>
            <p className="text-sm text-[#8A918D] mt-1">Scannez pour ouvrir le menu</p>
            <div ref={canvasWrapRef} className="mx-auto mt-5 w-fit p-4 bg-white rounded-2xl border border-[#EAE5DC]">
              {qrTable && <QRCodeCanvas value={qrUrl} size={200} fgColor="#123026" level="M" />}
            </div>
            <p className="text-[11px] text-[#B4B9B4] mt-3 break-all px-4">{qrUrl}</p>
            <Button data-testid="qr-download-btn" onClick={downloadQR} className="mt-5 w-full h-12 rounded-full bg-[#123026] hover:bg-[#0A1C16] text-white font-semibold">
              <Download className="w-4 h-4 mr-2" /> Télécharger le QR
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!billTable} onOpenChange={(o) => !o && setBillTable(null)}>
        <DialogContent data-testid="bill-dialog" className="max-w-sm rounded-3xl bg-[#FBF9F5]">
          <DialogTitle className="font-serif-display text-2xl text-[#123026]">Addition · Table {billTable?.number}</DialogTitle>
          <DialogDescription className="sr-only">Détail de l'addition par convive pour la table {billTable?.number}</DialogDescription>
          {loadingBill ? (
            <div className="py-10 grid place-items-center"><div className="w-6 h-6 rounded-full border-2 border-[#123026] border-t-transparent animate-spin" /></div>
          ) : bill && bill.order_count > 0 ? (
            <div>
              <p className="text-xs text-[#8A918D] mb-3">Détail par convive · préparez le partage</p>
              <div className="space-y-3">
                {bill.guests.map((g, i) => (
                  <div key={i} data-testid={`bill-guest-${i}`} className="rounded-2xl bg-white border border-[#EAE5DC] p-3">
                    <div className="flex justify-between items-center mb-1.5">
                      <span className="font-semibold text-[#123026]">👤 {g.guest_name}</span>
                      <span className="font-bold text-[#123026]">{eur(g.total)}</span>
                    </div>
                    <ul className="space-y-0.5">
                      {g.items.map((it, k) => (
                        <li key={k} className="text-xs text-[#8A918D] flex justify-between">
                          <span>{it.quantity}× {it.name}</span><span>{eur(it.line_total)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-center mt-4 pt-3 border-t border-[#EAE5DC]">
                <span className="font-serif-display text-lg text-[#123026]">Total table</span>
                <span data-testid="bill-grand-total" className="font-bold text-xl text-[#123026]">{eur(bill.grand_total)}</span>
              </div>
            </div>
          ) : (
            <p className="py-10 text-center text-sm text-[#8A918D]">Aucune commande active pour cette table.</p>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!forceTarget} onOpenChange={(o) => { if (!o) { setForceTarget(null); setForceReason(""); } }}>
        <DialogContent data-testid="force-close-dialog" className="max-w-md rounded-3xl bg-[#0C1713] border-[#1B3028] text-stone-100">
          <DialogTitle className="font-serif-display text-2xl flex items-center gap-2 text-red-300"><AlertTriangle className="w-5 h-5" /> Clôturer avec impayé · Table {forceTarget?.num}</DialogTitle>
          <DialogDescription className="text-stone-400">Il reste <span className="font-bold text-red-300">{eur(forceTarget?.remaining || 0)}</span> à encaisser. La clôture forcée exige un motif et une autorisation PIN (Manager/Propriétaire). L'action est tracée dans le Journal.</DialogDescription>
          <div className="pt-1">
            <label className="text-xs font-semibold uppercase tracking-wider text-stone-400">Motif (obligatoire)</label>
            <Input data-testid="force-close-reason" value={forceReason} onChange={(e) => setForceReason(e.target.value)} placeholder="Ex : client parti sans régler" className="mt-1.5 bg-[#111F1A] border-[#1B3028] text-stone-100 rounded-xl" />
            <Button data-testid="force-close-continue" onClick={() => { if (!forceReason.trim()) { toast.error("Motif obligatoire"); return; } setForcePinOpen(true); }}
              className="mt-4 w-full h-12 rounded-full bg-red-500 hover:bg-red-600 text-white font-semibold">
              Continuer avec le PIN
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <PinConfirmDialog open={forcePinOpen} onClose={() => setForcePinOpen(false)} slug={slug}
        title="Autoriser la clôture forcée"
        description="Saisissez le PIN Manager/Propriétaire pour clôturer une table impayée."
        onConfirmed={runForceClose} />
    </div>
  );
}
